import { useQueryClient } from '@tanstack/react-query'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { PSApplication } from '../components/app/PSApplication'
import { useErrorHandler } from '../hooks/useErrorHandler'
import { Edition } from '../models/Edition'
import { RecordFactory } from '../models/RecordFactory'
import { getNextOnboardingStepPath } from '../utils/defaults'

// Selections and customizations for the onboarding process

function useEdition() {
    const [edition, setEdition] = useState<Edition | undefined>(undefined)
    const [shouldAllowCopyDataStep, setShouldAllowCopyDataStep] = useState(false)

    useEffect(() => {
        ;(async () => {
            const user = PSApplication.me()
            const onboardingState = user.onboardingState ?? ''
            const edition = user.edition?.id ?? `${user.industry?.id}-industry-edition`
            const editionResult = await RecordFactory.getInstance().fetchEdition(
                edition,
                onboardingState || 'ob_hours'
            )
            if (editionResult.ok && editionResult.value) {
                setEdition(editionResult.value.edition)
                setShouldAllowCopyDataStep(editionResult.value.allowCopyData)
            }
        })()
    }, [])

    const hasDefaultCategories = useMemo(
        () => (edition ? Boolean(edition.defaultCategories?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultServices = useMemo(
        () => (edition ? Boolean(edition?.defaultServices?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultClasses = useMemo(
        () => (edition ? Boolean(edition?.defaultClasses?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultPackages = useMemo(
        () => (edition ? Boolean(edition?.defaultPackages?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultReservations = useMemo(
        () => (edition ? Boolean(edition?.defaultReservations?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultProducts = useMemo(
        () => (edition ? Boolean(edition?.defaultProducts?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultContracts = useMemo(
        () => (edition ? Boolean(edition?.defaultContracts?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultSubscriptions = useMemo(
        () => (edition ? Boolean(edition?.defaultSubscriptions?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultGifts = useMemo(
        () => (edition ? Boolean(edition?.defaultGifts?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultForms = useMemo(
        () => (edition ? Boolean(edition?.defaultTypes?.length ?? 0 > 0) : true),
        [edition]
    )

    const hasDefaultIntake = useMemo(
        () => (edition ? Boolean(edition?.defaultIntakeFields?.length ?? 0 > 0) : true),
        [edition]
    )

    return {
        edition,
        hasDefaultCategories,
        hasDefaultServices,
        hasDefaultClasses,
        hasDefaultPackages,
        hasDefaultReservations,
        hasDefaultProducts,
        hasDefaultContracts,
        hasDefaultSubscriptions,
        hasDefaultGifts,
        hasDefaultForms,
        hasDefaultIntake,
        shouldAllowCopyDataStep,
    }
}

const onboardingStates = {
    ob_hours: { next: 'ob_categories', path: '/onboarding/hours' },
    ob_select_data: { next: 'ob_categories', path: '/onboarding/copy' },
    ob_copy_data: { next: 'ob_location', path: '/onboarding/copy' },
    ob_categories: { next: 'ob_services', path: '/onboarding/categories' },
    ob_services: { next: 'ob_classes', path: '/onboarding/services' },
    ob_classes: { next: 'ob_reservations', path: '/onboarding/classes' },
    ob_reservations: { next: 'ob_packages', path: '/onboarding/reservations' },
    ob_packages: { next: 'ob_subscriptions', path: '/onboarding/packages' },
    ob_subscriptions: { next: 'ob_products', path: '/onboarding/subscriptions' },
    ob_products: { next: 'ob_gifts', path: '/onboarding/products' },
    ob_gifts: { next: 'ob_records', path: '/onboarding/gifts' },
    ob_records: { next: 'ob_contracts', path: '/onboarding/forms' },
    ob_contracts: { next: 'ob_intake', path: '/onboarding/contracts' },
    ob_intake: { next: 'ob_location', path: '/onboarding/intake' },
    ob_location: { next: 'ob_payments_1', path: '/onboarding/location' },
    ob_payments_1: { next: 'ob_payments_2', path: '/onboarding/payment' },
    ob_payments_2: { next: 'ob_profile', path: '/onboarding/payment-continued' },
    ob_profile: { next: 'ob_complete', path: '/onboarding/profile' },
    ob_complete: { next: 'gl_welcome', path: '/onboarding/success' },
    gl_welcome: { next: 'gl_business', path: '/golive/welcome' },
    gl_business: { next: 'gl_personal', path: '/golive/business-info' },
    gl_personal: { next: 'gl_payouts', path: '/golive/personal-info' },
    gl_payouts: { next: 'gl_complete', path: '/golive/receive-payouts' },
    // gl_premium: { next: 'gl_widget', path: '/golive/select-plan' },
    // gl_widget: { next: 'gl_complete', path: '/golive/widget' },
    gl_complete: { next: undefined, path: '/golive/success' },
} as const

export type OnboardingState = keyof typeof onboardingStates

type GrowYourBusiness = {
    yearsInBusiness?: string
    servicesOffered?: string
    marketingInfo?: string
    referralName?: string
    birthday?: Date
    ssnLast4?: string
    address?: string
    legalName?: string
}

type OnboardingContextType = {
    onboardingState?: OnboardingState
    advanceOnboardingState: (listings?: string[]) => Promise<void>
    growYourBusinessDetails: GrowYourBusiness | undefined
    setGrowYourBusinessDetails: React.Dispatch<React.SetStateAction<GrowYourBusiness | undefined>>
    didCopyFromOtherAccount: boolean
    setDidCopyFromOtherAccount: (didCopy: boolean) => void
}

const emptyOnboardingContext: OnboardingContextType = {
    onboardingState: undefined,
    advanceOnboardingState: async () => {},
    growYourBusinessDetails: undefined,
    setGrowYourBusinessDetails: () => {},
    didCopyFromOtherAccount: false,
    setDidCopyFromOtherAccount: () => {},
}

const OnboardingContext = createContext<OnboardingContextType>(emptyOnboardingContext)

function getCurrStateInfo(currStep: OnboardingState | undefined) {
    if (!currStep) return undefined
    return onboardingStates[currStep]
}

function findStateFromPath(path: string) {
    if (path === '/golive/conditionally-verified') return 'gl_personal'

    return Object.keys(onboardingStates).find(
        (key) => onboardingStates[key as OnboardingState].path === path
    ) as OnboardingState | undefined
}

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const queryClient = useQueryClient()

    const navigate = useNavigate()
    const { logAndDisplayError } = useErrorHandler()
    const [me, setMe] = useState(PSApplication.me())
    const {
        edition,
        hasDefaultCategories,
        hasDefaultServices,
        hasDefaultClasses,
        hasDefaultReservations,
        hasDefaultPackages,
        hasDefaultSubscriptions,
        hasDefaultProducts,
        hasDefaultGifts,
        hasDefaultContracts,
        hasDefaultForms,
        hasDefaultIntake,
        shouldAllowCopyDataStep,
    } = useEdition()

    const currentStateFromServer = me.onboardingState as OnboardingState | undefined

    const [onboardingState, setOnboardingState] = useState<OnboardingState | undefined>(
        currentStateFromServer ?? 'ob_hours'
    )

    const [businessInfo, setBusinessInfo] = useState<GrowYourBusiness | undefined>(undefined)
    const [didCopyFromOtherAccount, setDidCopyFromOtherAccount] = useState(false)

    const findNextStateContainingDefaults = useCallback(
        (currState: ReturnType<typeof getCurrStateInfo>): OnboardingState | undefined => {
            if (!currState) return undefined

            switch (currState.next) {
                case 'ob_categories':
                    return hasDefaultCategories
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_services':
                    return hasDefaultServices
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_classes':
                    return hasDefaultClasses
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_reservations':
                    return hasDefaultReservations
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_packages':
                    return hasDefaultPackages
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_subscriptions':
                    return hasDefaultSubscriptions
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_products':
                    return hasDefaultProducts
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_gifts':
                    return hasDefaultGifts
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_contracts':
                    return hasDefaultContracts
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_records':
                    return hasDefaultForms
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                case 'ob_intake':
                    return hasDefaultIntake
                        ? currState.next
                        : findNextStateContainingDefaults(getCurrStateInfo(currState.next))
                default:
                    return currState.next
            }
        },
        [
            hasDefaultCategories,
            hasDefaultServices,
            hasDefaultClasses,
            hasDefaultReservations,
            hasDefaultPackages,
            hasDefaultSubscriptions,
            hasDefaultProducts,
            hasDefaultGifts,
            hasDefaultContracts,
            hasDefaultForms,
            hasDefaultIntake,
        ]
    )

    /**
     * Determines the next step, saves the next step on the server, and navigates to the correct page
     */
    const advanceOnboardingState = useCallback(
        async (listings: string[] = []) => {
            const stateFromPath = findStateFromPath(location.pathname)

            // If we're on the hours step and the user has other associated business accounts, then we need
            // to redirect them to the step where they can choose to copy from another account or not.
            if (stateFromPath === 'ob_hours' && shouldAllowCopyDataStep) {
                const result =
                    await RecordFactory.getInstance().sendOnboardingState('ob_select_data')
                if (!result.ok) {
                    return logAndDisplayError(result.error)
                }
                setOnboardingState('ob_select_data')
                setMe((m) => m.clone({ onboarding_state: 'ob_select_data' }))
                return navigate(onboardingStates['ob_select_data'].path)
            }

            // If the user has selected to copy data from another account, then we need to navigate directly
            // to the location step afterward.
            if (stateFromPath === 'ob_select_data' && didCopyFromOtherAccount) {
                const result = await RecordFactory.getInstance().sendOnboardingState('ob_location')
                if (!result.ok) {
                    return logAndDisplayError(result.error)
                }
                setOnboardingState('ob_location')
                setMe((m) => m.clone({ onboarding_state: 'ob_location' }))
                return navigate(onboardingStates['ob_location'].path)
            }

            // if we're not on a known onboarding page, navigate to our onboarding state
            if (!stateFromPath) {
                if (onboardingState === 'ob_complete') {
                    return navigate('/golive/welcome')
                }
                return navigate(getCurrStateInfo(onboardingState)?.path ?? '/onboarding/hours')
            }

            const navigateToCategoryDefaults =
                Boolean(searchParams.get('recordIDs')) ||
                (stateFromPath === 'ob_categories' && listings.length)

            // if we're navigating from the category state, we need to go by defaults
            if (edition && navigateToCategoryDefaults) {
                const potentialItemIDs = [
                    ...(searchParams.get('recordIDs')?.split(',') ?? []),
                    ...listings,
                ]

                const path = await getNextOnboardingStepPath({
                    edition,
                    potentialItemIDs,
                    step: stateFromPath,
                })

                if (path) {
                    return navigate(path)
                }

                // if there is no path, we're done with category defaults and can move on to intake
                const result = await RecordFactory.getInstance().sendOnboardingState('ob_intake')
                if (!result.ok) {
                    return logAndDisplayError(result.error)
                }
                setOnboardingState('ob_intake')
                setMe((m) => m.clone({ onboarding_state: 'ob_intake' }))
                return navigate('/onboarding/intake')
            }

            // if we're not on the current onboarding state, navigate to the next step page but don't update server state
            // gl_welcome is a special case because it's not a real onboarding state according to the server
            if (stateFromPath !== onboardingState && stateFromPath !== 'gl_welcome') {
                const nextState = findNextStateContainingDefaults(getCurrStateInfo(stateFromPath))

                if (!nextState) {
                    return navigate('/')
                }

                setOnboardingState(nextState)
                return navigate(onboardingStates[nextState].path)
            }

            let currState = getCurrStateInfo(onboardingState)
            if (!currState) {
                // in this scenario, where a user has no onboarding state, start them off at state 1
                const result = await RecordFactory.getInstance().sendOnboardingState('ob_hours')
                if (!result.ok) {
                    return logAndDisplayError(result.error)
                }
                setOnboardingState('ob_hours')
                setMe((m) => m.clone({ onboarding_state: 'ob_hours' }))
                return navigate(onboardingStates['ob_hours'].path)
            }

            const nextStateContainingDefaults = findNextStateContainingDefaults(currState)

            // if there is a next state, update the server & react state
            if (nextStateContainingDefaults) {
                await RecordFactory.getInstance().sendOnboardingState(nextStateContainingDefaults)
                setOnboardingState(nextStateContainingDefaults)
                setMe((m) => m.clone({ onboarding_state: nextStateContainingDefaults }))
            }

            // ensure we invalidate the dashboard query so that we can show the correct dashboards after onboarding has completed
            queryClient.invalidateQueries({ queryKey: ['dashboard'] })

            const finalPath = onboardingStates[nextStateContainingDefaults ?? 'gl_complete'].path
            // finally, navigate to the next page
            // in the case of `gl_complete`, we don't update the state (because there is no next state) but we do navigate the page
            navigate(finalPath)
        },
        [
            edition,
            onboardingState,
            navigate,
            location,
            findNextStateContainingDefaults,
            searchParams,
            queryClient,
            didCopyFromOtherAccount,
            shouldAllowCopyDataStep,
        ]
    )

    return (
        <OnboardingContext.Provider
            value={{
                onboardingState,
                advanceOnboardingState,
                growYourBusinessDetails: businessInfo,
                setGrowYourBusinessDetails: setBusinessInfo,
                didCopyFromOtherAccount,
                setDidCopyFromOtherAccount,
            }}
        >
            {children}
        </OnboardingContext.Provider>
    )
}

export function useOnboardingContext() {
    return useContext(OnboardingContext)
}
